import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  // Skip token append on auth endpoints (login/forgot/reset must be public + clean)
  const isAuthPublic = /\/auth\/(login|forgot-password|reset-password|set-password)/.test(config.url || '');
  if (!isAuthPublic) {
    const token = localStorage.getItem('token');
    if (token) {
      const separator = config.url.includes('?') ? '&' : '?';
      config.url = `${config.url}${separator}token=${token}`;
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on auth-flow endpoints — let the page show its own error toast
    const failedUrl = error.config?.url || '';
    const isAuthFlow = /\/auth\/(login|forgot-password|reset-password|set-password|change-password)/.test(failedUrl);
    if (error.response?.status === 401 && !isAuthFlow) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  setPassword: (data) => axios.post(`${API_BASE}/api/auth/reset-password`, data),
  forgotPassword: (data) => axios.post(`${API_BASE}/api/auth/forgot-password`, data),
  changePassword: (data) => api.post('/auth/change-password', data),
  updateProfile: (data) => api.put('/auth/profile', data),
  acceptTerms: () => api.post('/auth/accept-terms'),
};

export const twoFactorAPI = {
  // Public (uses partial_token in body)
  requestCode: (data) => axios.post(`${API_BASE}/api/auth/2fa/request-code`, data),
  verify: (data) => axios.post(`${API_BASE}/api/auth/2fa/verify`, data),
  // Authenticated
  status: () => api.get('/auth/2fa/status'),
  enable: (method) => api.post('/auth/2fa/enable', { method }),
  disable: () => api.post('/auth/2fa/disable'),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  block: (id) => api.post(`/users/${id}/block`),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`),
  generateCredentials: (id) => api.post(`/users/${id}/generate-credentials`),
  setPassword: (id, password) => api.post(`/users/${id}/set-password`, { password }),
  getLoginHistory: (id) => api.get(`/users/${id}/login-history`),
};

export const clientsAPI = {
  getAll: (params) => api.get('/clients', { params: { limit: 10000, ...(params || {}) } }),
  search: (query) => api.get(`/clients/search?q=${encodeURIComponent(query)}`),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  bulkAction: (data) => api.post('/clients/bulk-action', data),
  gdprDelete: (id) => api.delete(`/clients/${id}/gdpr`),
  getLoyaltyCards: (clientId) => api.get(`/clients/${clientId}/loyalty-cards`),
  createLoyaltyCard: (clientId, data) => api.post(`/clients/${clientId}/loyalty-cards`, data),
  // Last contact map for "Ostatni kontakt" column
  lastContacts: (ids) => {
    const qs = ids && ids.length ? `?ids=${ids.join(',')}` : '';
    return api.get(`/clients/last-contacts${qs}`);
  },
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/clients/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  smartImportPreview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/clients/smart-import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  smartImportCommit: (payload) => api.post('/clients/smart-import/commit', payload),
  smartImportUndo: (importId) => api.post(`/clients/smart-import/${importId}/undo`),
  smartImportMergeLog: (importId) => api.get(`/clients/smart-import/${importId}/merge-log`),
  smartImportHistory: () => api.get('/clients/smart-import/history'),
  smartImportHistoryItem: (id) => api.get(`/clients/smart-import/history/${id}`),
  smartImportTemplateUrl: () => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/api/clients/smart-import/template?token=${token}`;
  },
  smartImportReportCsvUrl: (importId) => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/api/clients/smart-import/history/${importId}/report.csv?token=${token}`;
  },
  downloadTemplate: () => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/api/clients/import/template?token=${token}`;
  },
  exportClients: (params = {}) => {
    const token = localStorage.getItem('token');
    const queryParams = new URLSearchParams(params).toString();
    return `${API_BASE}/api/clients/export?token=${token}&${queryParams}`;
  },
};

export const messagesAPI = {
  // Unified communication history per client
  getClientMessages: (clientId, params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined && v !== '' && v !== null))
    ).toString();
    return api.get(`/clients/${clientId}/messages${qs ? `?${qs}` : ''}`);
  },
  // Unified send: { client_id, channel: 'sms'|'whatsapp'|'email', content, subject? }
  send: (data) => api.post('/messages/send', data),
  templates: () => api.get('/messages/templates'),
};

export const tourOperatorsAPI = {
  getAll: () => api.get('/tour-operators'),
  create: (data) => api.post('/tour-operators', data),
  update: (id, data) => api.put(`/tour-operators/${id}`, data),
  delete: (id) => api.delete(`/tour-operators/${id}`),
};

export const branchesAPI = {
  getAll: () => api.get('/branches'),
  create: (data) => api.post('/branches', data),
  update: (id, data) => api.put(`/branches/${id}`, data),
  delete: (id) => api.delete(`/branches/${id}`),
};

export const bookingsAPI = {
  getAll: (params = {}) => api.get('/bookings', { params: { limit: 20000, ...params } }),
  getOne: (id) => api.get(`/bookings/${id}`),
  create: (data) => api.post('/bookings', data),
  update: (id, data) => api.put(`/bookings/${id}`, data),
  delete: (id) => api.delete(`/bookings/${id}`),
  setPaymentStatus: (id, payment_status) => api.patch(`/bookings/${id}/payment-status`, { payment_status }),
  search: (filters) => api.post('/bookings/search', filters),
  getChecks: (id) => api.get(`/bookings/${id}/checks`),
  updateCheck: (id, data) => api.post(`/bookings/${id}/checks`, data),
  getClientLoyaltyCard: (id) => api.get(`/bookings/${id}/client-loyalty-card`),
  import: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bookings/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  bulkArchive: (booking_ids) => api.post('/bookings/bulk/archive', { booking_ids }),
  bulkRestore: (booking_ids) => api.post('/bookings/bulk/restore', { booking_ids }),
  bulkDelete: (booking_ids, confirm) => api.post('/bookings/bulk/delete', { booking_ids, confirm }),
  bulkChangeStatus: (booking_ids, status) => api.post('/bookings/bulk/change-status', { booking_ids, status }),
  bulkChangeEmployee: (booking_ids, employee_id) => api.post('/bookings/bulk/change-employee', { booking_ids, employee_id }),
  bulkExport: (booking_ids) =>
    api.post('/bookings/bulk/export', { booking_ids }, { responseType: 'blob' }),
  importPreview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bookings/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadTemplate: () => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/api/bookings/import/template?token=${token}`;
  },
};

export const loyaltyCardsAPI = {
  update: (cardId, data) => api.put(`/loyalty-cards/${cardId}`, data),
  delete: (cardId) => api.delete(`/loyalty-cards/${cardId}`),
};

export const paymentsAPI = {
  getAll: (params) => {
    if (typeof params === 'string') {
      // legacy: bookingId as string
      return api.get(params ? `/payments?booking_id=${params}` : '/payments');
    }
    return api.get('/payments', { params });
  },
  getOne: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
};

export const companiesAPI = {
  getAll: (params) => api.get('/companies', { params }),
  lookup: (nip) => api.get(`/companies/lookup/${nip}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
};

export const vouchersAPI = {
  getAll: (params) => api.get('/vouchers', { params }),
  getOne: (id) => api.get(`/vouchers/${id}`),
  create: (data) => api.post('/vouchers', data),
  update: (id, data) => api.put(`/vouchers/${id}`, data),
  delete: (id) => api.delete(`/vouchers/${id}`),
  use: (id, data) => api.post(`/vouchers/${id}/use`, data),
  getClientVouchers: (clientId, activeOnly = false) => api.get(`/clients/${clientId}/vouchers?active_only=${activeOnly}`),
  getReport: () => api.get('/vouchers/report/summary'),
  downloadPdf: (id) => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/api/vouchers/${id}/pdf?token=${token}`;
  },
};

export const invoicesAPI = {
  getAll: () => api.get('/invoices'),
  getOne: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  cancel: (id) => api.post(`/invoices/${id}/cancel`),
  send: (id) => api.post(`/invoices/${id}/send`),
  sendEmail: (id, toEmail, message) => api.post(`/invoices/${id}/send`, null, {
    params: { to_email: toEmail, message: message }
  }),
  addPayment: (id, amount) => api.post(`/invoices/${id}/add-payment?amount=${amount}`),
  downloadPdf: (id) => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/api/invoices/${id}/pdf?token=${token}`;
  },
  downloadXml: (id, fmt = 'standard') => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/api/invoices/${id}/xml?fmt=${fmt}&token=${token}`;
  },
  downloadJson: (id) => api.get(`/invoices/${id}/json`),
  pdfDuplicate: (id, copies = 2) => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/api/invoices/${id}/pdf-copies?copies=${copies}&token=${token}`;
  },
  share: (id, expiresDays = 30) => api.post(`/invoices/${id}/share`, null, {
    params: { expires_days: expiresDays },
  }),
  listShares: (id) => api.get(`/invoices/${id}/shares`),
  revokeShare: (id, token) => api.delete(`/invoices/${id}/share/${token}`),
};

// === Calendar v3 — strict private + sharing ===
export const calendarV3API = {
  eventTypes: () => api.get('/calendar/event-types'),
  list: (params) => api.get('/calendar/events', { params }),
  get: (id) => api.get(`/calendar/events/${id}`),
  create: (data) => api.post('/calendar/events', data),
  update: (id, data) => api.put(`/calendar/events/${id}`, data),
  delete: (id) => api.delete(`/calendar/events/${id}`),
  share: (id, shared_with_users) => api.post(`/calendar/events/${id}/share`, { shared_with_users }),
  usersForSharing: () => api.get('/calendar/users-for-sharing'),
  migrateLegacy: () => api.post('/calendar/migrate-legacy'),
};

// === Faktury v2 — Fakturownia-style + KSeF ===
export const contractorsAPI = {
  types: () => api.get('/contractors/types'),
  list: (params) => api.get('/contractors', { params }),
  get: (id) => api.get(`/contractors/${id}`),
  create: (data) => api.post('/contractors', data),
  update: (id, data) => api.put(`/contractors/${id}`, data),
  delete: (id) => api.delete(`/contractors/${id}`),
  fromNipLookup: (data) => api.post('/contractors/from-nip-lookup', data),
};

export const invoicesV2API = {
  types: () => api.get('/invoices-v2/types'),
  dashboard: () => api.get('/invoices-v2/dashboard'),
  getNumberingConfig: () => api.get('/invoices-v2/numbering-config'),
  updateNumberingConfig: (data) => api.put('/invoices-v2/numbering-config', data),
  peekNumber: (invoiceType, branchId) => api.get('/invoices-v2/peek-number', {
    params: { invoice_type: invoiceType, branch_id: branchId }
  }),
  nbpRate: (currency, date) => api.get('/invoices-v2/nbp-rate', {
    params: { currency, date }
  }),
  create: (data) => api.post('/invoices-v2', data),
  convertToVat: (id) => api.post(`/invoices-v2/${id}/convert-to-vat`),
  createCorrection: (id, data) => api.post(`/invoices-v2/${id}/create-correction`, data),
  createFinal: (id) => api.post(`/invoices-v2/${id}/create-final`),
  ksefValidate: (id) => api.post(`/invoices-v2/${id}/ksef-validate`),
  ksefXmlUrl: (id) => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/api/invoices-v2/${id}/ksef-xml?token=${token}`;
  },
  ksefSend: (id, signNow = false) => api.post(`/invoices-v2/${id}/ksef-send`, { sign_now: signNow }),
  timeline: (id) => api.get(`/invoices-v2/${id}/timeline`),
};

export const nipLookupAPI = {
  lookup: (nip) => api.get(`/nip-lookup/${nip}`),
};

export const cashReportAPI = {
  get: (params) => api.get('/cash-report', { params }),
};

export const workHoursAPI = {
  getAll: (params) => api.get('/work-hours', { params }),
  record: (data) => api.post('/work-hours', data),
  getCurrent: () => api.get('/work-hours/current'),
  approve: (id) => api.put(`/work-hours/${id}/approve`),
  reject: (id, comment) => api.put(`/work-hours/${id}/reject?comment=${encodeURIComponent(comment || '')}`),
  update: (id, data) => api.put(`/work-hours/${id}`, data),
  delete: (id) => api.delete(`/work-hours/${id}`),
};

export const leavesAPI = {
  getAll: (params) => api.get('/leaves', { params }),
  create: (data) => api.post('/leaves', data),
  update: (id, data) => api.put(`/leaves/${id}`, data),
  approve: (id) => api.put(`/leaves/${id}/approve`),
  approveWithNotification: (id) => api.put(`/leaves/${id}/approve-with-notification`),
  reject: (id, comment) => api.put(`/leaves/${id}/reject?comment=${encodeURIComponent(comment || '')}`),
  cancel: (id, comment) => api.put(`/leaves/${id}/cancel?comment=${encodeURIComponent(comment || '')}`),
  delete: (id) => api.delete(`/leaves/${id}`),
  getBalance: (employeeId, year) => api.get(`/leaves/balance/${employeeId}`, { params: { year } }),
  getEmployeeProfile: (employeeId) => api.get(`/leaves/employee-profile/${employeeId}`),
  validate: (data) => api.post('/leaves/validate', data),
  bulkAction: (data) => api.post('/leaves/bulk-action', data),
  adminSign: (id, signature, approve = true) => api.post(`/leaves/${id}/admin-sign`, { signature, approve }),
  cancel: (id, reason = '') => api.post(`/leaves/${id}/cancel`, { reason }),
  getCalendar: (params) => api.get('/leaves/calendar', { params }),
  downloadPdf: (id) => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/api/leaves/${id}/pdf?token=${token}`;
  },
  // v47 — attachments
  listAttachments: (id) => api.get(`/leaves/${id}/attachments`),
  uploadAttachment: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/leaves/${id}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadAttachment: (id, filename) => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/api/leaves/${id}/attachments/${encodeURIComponent(filename)}?token=${token}`;
  },
  deleteAttachment: (id, filename) =>
    api.delete(`/leaves/${id}/attachments/${encodeURIComponent(filename)}`),
};

export const signatureAPI = {
  get: () => api.get('/users/me/signature'),
  save: (dataUrl) => api.put('/users/me/signature', { signature_b64: dataUrl }),
  clear: () => api.delete('/users/me/signature'),
};

export const calendarEventsAPI = {
  getAll: (params) => api.get('/calendar-events', { params }),
  create: (data) => api.post('/calendar-events', data),
  delete: (id) => api.delete(`/calendar-events/${id}`),
};

export const birthdayAPI = {
  getToday: () => api.get('/birthdays/today'),
  sendGreetings: () => api.post('/birthdays/send-greetings'),
};

export const emailQueueAPI = {
  getAll: (params) => api.get('/email-queue', { params }),
};

export const calendarAPI = {
  getData: (params) => api.get('/calendar', { params }),
};

export const dailyReportsAPI = {
  getAll: (date) => api.get(date ? `/daily-reports?date=${date}` : '/daily-reports'),
  create: (data) => api.post('/daily-reports', data),
};

export const reportsAPI = {
  get: (params) => api.get('/reports', { params }),
};

export const salesStatsAPI = {
  get: (params) => api.get('/sales-stats', { params }),
};

export const commissionsAPI = {
  get: (month, year) => api.get(`/commissions?month=${month}&year=${year}`),
  getYearly: (year) => api.get(`/commissions/yearly?year=${year}`),
  getEmployee: (employeeId) => api.get(`/commissions/employee/${employeeId}`),
  updateSettings: (data) => api.put('/commissions/settings', data),
};

export const promotionsAPI = {
  getAll: (activeOnly) => api.get(activeOnly ? '/promotions?active_only=true' : '/promotions'),
  create: (data) => api.post('/promotions', data),
  update: (id, data) => api.put(`/promotions/${id}`, data),
  delete: (id) => api.delete(`/promotions/${id}`),
};

export const smsTemplatesAPI = {
  getAll: () => api.get('/sms-templates'),
};

export const emailLogAPI = {
  getAll: () => api.get('/email-log'),
};

export const auditLogAPI = {
  getAll: (params) => api.get('/audit-log', { params }),
};

export const clientPortalAPI = {
  login: (bookingNumber, birthDate) => axios.post(`${API_BASE}/api/client-portal/login`, { booking_number: bookingNumber, birth_date: birthDate }),
  getBooking: (token) => axios.get(`${API_BASE}/api/client-portal/booking?token=${token}`),
  downloadContract: (token) => axios.get(`${API_BASE}/api/client-portal/contract?token=${token}`, { responseType: 'blob' }),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getUpcomingDepartures: () => api.get('/dashboard/upcoming-departures'),
  getBirthdays: (days = 7) => api.get(`/dashboard/birthdays?days=${days}`),
  updateBookingFlags: (bookingId, flags) => api.patch(`/bookings/${bookingId}/status-flags`, flags),
};

export const settingsAPI = {
  getLogo: () => api.get('/settings/logo'),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/upload-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteLogo: () => api.delete('/settings/logo'),
};

export const contractsAPI = {
  getAll: (params) => api.get('/contracts', { params }),
  getOne: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts/generate', data),
  uploadPdf: (formData) => api.post('/contracts/upload-pdf', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  sign: (id, data) => api.post(`/contracts/${id}/sign`, data),
  send: (id) => api.post(`/contracts/${id}/send`),
  delete: (id) => api.delete(`/contracts/${id}`),
  getAlerts: () => api.get('/contracts/alerts'),
  downloadPdf: (id) => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/api/contracts/${id}/pdf?token=${token}`;
  },
};

export const pushAPI = {
  getVapidKey: () => api.get('/push/vapid-key'),
  subscribe: (data) => api.post('/push/subscribe', data),
  unsubscribe: () => api.delete('/push/unsubscribe'),
};

export const unreadAPI = {
  getCount: () => api.get('/chat/unread-count'),
};

export const employeeStatsAPI = {
  get: (period) => api.get('/employee-stats', { params: { period } }),
  // Extended P1 KPI per employee with date range + filters
  getKpi: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined && v !== '' && v !== null))
    ).toString();
    return api.get(`/kpi/employees${qs ? `?${qs}` : ''}`);
  },
};

export const adminAPI = {
  getOrphanPayments: () => api.get('/admin/orphan-payments'),
  reassignOrphanPayments: (data) => api.post('/admin/orphan-payments/reassign', data),
};

export const campaignsAPI = {
  list: () => api.get('/campaigns'),
  create: (data) => api.post('/campaigns', data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  remove: (id) => api.delete(`/campaigns/${id}`),
  preview: (id) => api.post(`/campaigns/${id}/preview`),
  run: (id) => api.post(`/campaigns/${id}/run`),
  runs: (id, limit = 100) => api.get(`/campaigns/${id}/runs?limit=${limit}`),
};

export const settlementsAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined && v !== '' && v !== null))
    ).toString();
    return api.get(`/settlements${qs ? `?${qs}` : ''}`);
  },
  get: (id) => api.get(`/settlements/by-id/${id}`),
  create: (data) => api.post('/settlements', data),
  update: (id, data) => api.put(`/settlements/by-id/${id}`, data),
  remove: (id) => api.delete(`/settlements/by-id/${id}`),
  unsettled: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined && v !== '' && v !== null))
    ).toString();
    return api.get(`/settlements/unsettled-payments${qs ? `?${qs}` : ''}`);
  },
  balance: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined && v !== '' && v !== null))
    ).toString();
    return api.get(`/settlements/balance${qs ? `?${qs}` : ''}`);
  },
  reconciliation: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined && v !== '' && v !== null))
    ).toString();
    return api.get(`/settlements/reconciliation${qs ? `?${qs}` : ''}`);
  },
  scanMissingCash: () => api.post('/settlements/scan-missing-cash'),
  depositTodayPreview: () => api.get('/settlements/deposit-today/preview'),
  depositToday: (notes = '') => api.post(`/settlements/deposit-today${notes ? `?notes=${encodeURIComponent(notes)}` : ''}`),
  // Manual deposit (wpłata ręczna, bez powiązania z payments)
  createManual: (data) => api.post('/settlements/manual', data),
  // Status workflow with history log
  changeStatus: (id, data) => api.put(`/settlements/by-id/${id}/status`, data),
  // Settlement items (granular per-reservation lines, 1:N per settlement)
  listItems: (id) => api.get(`/settlements/by-id/${id}/items`),
  addItem: (id, data) => api.post(`/settlements/by-id/${id}/items`, data),
  updateItem: (itemId, data) => api.put(`/settlements/items/${itemId}`, data),
  deleteItem: (itemId) => api.delete(`/settlements/items/${itemId}`),
  // Float (drobne) operations
  getFloat: (employeeId) => api.get(`/settlements/float/${employeeId}`),
  floatMovements: (employeeId, limit = 50) => api.get(`/settlements/float-movements/${employeeId}?limit=${limit}`),
  assignFloat: (data) => api.post('/settlements/float/assign', data),
  useFloat: (data) => api.post('/settlements/float/use', data),
  returnFloat: (data) => api.post('/settlements/float/return', data),
  setFloatThreshold: (employeeId, threshold) => api.put(`/settlements/float/threshold/${employeeId}`, { threshold }),
};

export const bookingFinanceAPI = {
  get: (bookingId) => api.get(`/bookings/${bookingId}/finance`),
};

export const trainingsAPI = {
  list: (section) => api.get(`/trainings${section ? `?section=${section}` : ''}`),
  overview: () => api.get('/trainings/overview'),
  get: (id) => api.get(`/trainings/${id}`),
  updateProgress: (id, data) => api.post(`/trainings/${id}/progress`, data),
  submitTest: (id, answers) => api.post(`/trainings/${id}/submit-test`, { answers }),
  context: (key) => api.get(`/trainings/context/${key}`),
  logHintEvent: (event, key) => api.post(`/trainings/hint-event?event=${event}&key=${encodeURIComponent(key)}`),
  create: (data) => api.post('/trainings', data),
  update: (id, data) => api.put(`/trainings/${id}`, data),
  remove: (id) => api.delete(`/trainings/${id}`),
};

export const notificationsAPI = {
  list: (params = {}) => api.get('/notifications', { params }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
  snooze: (id, minutes = 15) => api.post(`/notifications/${id}/snooze`, null, { params: { minutes } }),
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (data) => api.put('/notifications/preferences', data),
  runRemindersNow: () => api.post('/admin/reminders/run-now'),
};

export const operatorPaymentsAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined && v !== '' && v !== null))
    ).toString();
    return api.get(`/operator-payments${qs ? `?${qs}` : ''}`);
  },
  forBooking: (bookingId) => api.get(`/bookings/${bookingId}/operator-payments`),
  create: (data) => api.post('/operator-payments', data),
  update: (id, data) => api.put(`/operator-payments/${id}`, data),
  remove: (id) => api.delete(`/operator-payments/${id}`),
};

export const cashFlowAPI = {
  get: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined && v !== '' && v !== null))
    ).toString();
    return api.get(`/dashboard/cash-flow${qs ? `?${qs}` : ''}`);
  },
};

export const paymentApprovalAPI = {
  approve: (paymentId) => api.post(`/payments/${paymentId}/approve`),
};

export const branchDashboardAPI = {
  get: (branchId) => api.get('/branch-dashboard', { params: branchId ? { branch_id: branchId } : {} }),
};

export const ownerDashboardAPI = {
  get: () => api.get('/owner-dashboard'),
};

export const franchiseSettlementsAPI = {
  list: (params = {}) => api.get('/franchise-settlements', { params }),
  get: (id) => api.get(`/franchise-settlements/${id}`),
  generateMonth: (data) => api.post('/franchise-settlements/generate-month', data),
  generateSingle: (data) => api.post('/franchise-settlements/generate', data),
  update: (id, data) => api.patch(`/franchise-settlements/${id}`, data),
  alerts: () => api.get('/franchise-settlements/alerts'),
  pdfUrl: (id) => `${API_BASE}/api/franchise-settlements/${id}/pdf?token=${localStorage.getItem('token')}`,
};

export const networkEventsAPI = {
  recent: (since, limit = 20) => api.get('/network-events/recent', { params: { since, limit } }),
};

export const teamCommissionsAPI = {
  listConfigs: (params = {}) => api.get('/team-commissions/configs', { params }),
  createConfig: (data) => api.post('/team-commissions/configs', data),
  updateConfig: (id, data) => api.put(`/team-commissions/configs/${id}`, data),
  deleteConfig: (id) => api.delete(`/team-commissions/configs/${id}`),
  dashboard: (month) => api.get('/team-commissions/dashboard', { params: month ? { month } : {} }),
  payout: (employeeId, month) => api.get(`/team-commissions/payouts/${employeeId}`, { params: { month } }),
  payoutPdfUrl: (employeeId, month) => `${API_BASE}/api/team-commissions/payouts/${employeeId}/pdf?month=${month}&token=${localStorage.getItem('token')}`,
  recalculate: (params = {}) => api.post('/team-commissions/recalculate', null, { params }),
};

export const factoryResetAPI = {
  preview: (data) => api.post('/admin/factory-reset/preview', data),
  run: (data) => api.post('/admin/factory-reset/run', data),
  history: () => api.get('/admin/factory-reset/runs'),
  downloadBackup: (filename) => `${API_BASE}/api/admin/factory-reset/backups/${encodeURIComponent(filename)}?token=${localStorage.getItem('token')}`,
};

export default api;
